import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  BookOpen, ArrowDown, ArrowUp, Bot, Building2, CalendarClock, Check, Clock, Eye, GitBranch, LayoutGrid, Lock, Plus,
  Save, Sparkles, Tags, Trash2, Users,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/insights/Skeleton';
import { UnderlineTabs } from '@/components/Listing';
import { INDUSTRY_OPTIONS } from '@/config/industries';
import {
  BOT_MODE_META, Blueprint, BlueprintRegistry, BlueprintUpdate, BotMode, CrmLabel, DeptKind, StageTerminal, WEEKDAYS,
  blueprintsService,
} from '@/services/blueprints.service';
import { TemplateEntityType, TemplateFieldKind } from '@/services/industry-configs.service';
import { ENTITY_META, ENTITY_ORDER, KIND_META, hasOptions, industryIcon, timeAgo } from '@/pages/industry-configs/industry-meta';
import { Draft, FieldCard, QUICK_KINDS, keyOf, newUid, toDrafts, toPayload } from '@/pages/industry-configs/FieldEditor';
import { DEPT_KINDS, GROUP_WORD, LABEL_ORDER, PAGE_WORD, familyIcon, wordOf } from './blueprint-meta';

type TabKey = 'pages' | 'fields' | 'pipeline' | 'departments' | 'services' | 'types';

interface StageDraft { uid: string; name: string; terminal: StageTerminal; probability: number; color: string; rotDays: string }
interface DeptDraft { uid: string; name: string; kind: DeptKind; description: string; color: string; icon?: string }
interface ServiceDraft { uid: string; name: string; category: string; durationMinutes: string; price: string; description: string; bookable: boolean; kind: 'product' | 'service'; inCatalog: boolean }
interface EditorDraft {
  name: string;
  description: string;
  industries: string[];
  pages: Record<string, boolean>;
  labels: Record<string, { singular: string; plural: string }>;
  pipelineName: string;
  stages: StageDraft[];
  departments: DeptDraft[];
  services: ServiceDraft[];
  hours: { days: number[]; start: string; end: string };
  aiBotMode: BotMode;
}

const STAGE_COLORS = ['#60a5fa', '#a78bfa', '#fbbf24', '#fb923c', '#f472b6', '#34d399'];

function toEditor(b: Blueprint): EditorDraft {
  return {
    name: b.name,
    description: b.description ?? '',
    industries: [...b.industries],
    pages: { ...b.pages },
    labels: Object.fromEntries(Object.entries(b.labels ?? {}).map(([k, v]) => [k, { ...v }])),
    pipelineName: b.pipeline?.name ?? '',
    stages: (b.pipeline?.stages ?? []).map((s, i) => ({
      uid: newUid(), name: s.name, terminal: s.terminal, probability: s.probability,
      color: s.color ?? STAGE_COLORS[i % STAGE_COLORS.length], rotDays: s.rotDays ? String(s.rotDays) : '',
    })),
    departments: b.departments.map((d) => ({ uid: newUid(), name: d.name, kind: d.kind, description: d.description ?? '', color: d.color ?? '#a18a44', icon: d.icon })),
    services: b.sampleServices.map((s) => ({
      uid: newUid(), name: s.name, category: s.category ?? '', durationMinutes: String(s.durationMinutes),
      price: s.price !== undefined && s.price !== null ? String(s.price) : '', description: s.description ?? '', bookable: s.bookable,
      kind: s.kind ?? (s.bookable ? 'service' : 'product'), inCatalog: s.inCatalog !== false,
    })),
    hours: { days: [...(b.serviceHours?.days ?? [1, 2, 3, 4, 5])], start: b.serviceHours?.start ?? '09:00', end: b.serviceHours?.end ?? '18:00' },
    aiBotMode: b.aiBotMode ?? 'flow',
  };
}

function toUpdate(d: EditorDraft, fields: Draft[], defaults: Record<string, CrmLabel>): BlueprintUpdate {
  const labels: Record<string, CrmLabel> = {};
  for (const [k, v] of Object.entries(d.labels)) {
    const plural = v.plural.trim();
    const singular = v.singular.trim();
    if (!plural && !singular) continue;
    const next = { plural: plural || defaults[k]?.plural || k, singular: singular || defaults[k]?.singular || k };
    if (next.plural !== defaults[k]?.plural || next.singular !== defaults[k]?.singular) labels[k] = next;
  }
  return {
    name: d.name.trim(),
    description: d.description.trim(),
    industries: d.industries,
    pages: d.pages,
    labels,
    fields: ENTITY_ORDER.flatMap((e) => fields.filter((f) => f.entityType === e).map((f, i) => toPayload(f, i))),
    pipeline: {
      name: d.pipelineName.trim(),
      stages: d.stages.map((s) => ({
        name: s.name.trim(), terminal: s.terminal,
        probability: s.terminal === 'won' ? 100 : s.terminal === 'lost' ? 0 : Math.max(0, Math.min(100, Math.round(Number(s.probability) || 0))),
        color: s.color,
        ...(s.rotDays && Number(s.rotDays) > 0 ? { rotDays: Math.round(Number(s.rotDays)) } : {}),
      })),
    },
    departments: d.departments.map((x) => ({
      name: x.name.trim(), kind: x.kind, color: x.color,
      ...(x.description.trim() ? { description: x.description.trim() } : {}), ...(x.icon ? { icon: x.icon } : {}),
    })),
    sampleServices: d.services.map((s) => ({
      name: s.name.trim(), durationMinutes: Math.max(5, Math.round(Number(s.durationMinutes) || 30)), bookable: s.bookable,
      kind: s.bookable ? 'service' as const : s.kind, inCatalog: s.inCatalog,
      ...(s.category.trim() ? { category: s.category.trim() } : {}),
      ...(s.description.trim() ? { description: s.description.trim() } : {}),
      ...(s.price.trim() !== '' && Number.isFinite(Number(s.price)) ? { price: Number(s.price) } : {}),
    })),
    serviceHours: { days: [...d.hours.days].sort(), start: d.hours.start, end: d.hours.end },
    aiBotMode: d.aiBotMode,
  };
}

export default function BlueprintEditor() {
  const { familyKey = '' } = useParams();
  const nav = useNavigate();
  const [registry, setRegistry] = useState<BlueprintRegistry | null>(null);
  const [others, setOthers] = useState<Blueprint[]>([]);
  const [saved, setSaved] = useState<Blueprint | null>(null);
  const [missing, setMissing] = useState(false);
  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [fields, setFields] = useState<Draft[]>([]);
  const [baseline, setBaseline] = useState('');
  const [tab, setTab] = useState<TabKey>('pages');
  const [entity, setEntity] = useState<TemplateEntityType>('lead');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focusUid, setFocusUid] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback((b: Blueprint, reg: BlueprintRegistry) => {
    const d = toEditor(b);
    const f = toDrafts({ industryKey: b.familyKey, label: b.name, fields: b.fields });
    setSaved(b);
    setDraft(d);
    setFields(f);
    setBaseline(JSON.stringify(toUpdate(d, f, reg.labels)));
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([blueprintsService.registry(), blueprintsService.list()])
      .then(([reg, list]) => {
        if (cancelled) return;
        const b = list.find((x) => x.familyKey === familyKey);
        setRegistry(reg);
        setOthers(list.filter((x) => x.familyKey !== familyKey));
        if (!b) { setMissing(true); return; }
        load(b, reg);
      })
      .catch(() => setMissing(true));
    return () => { cancelled = true; };
  }, [familyKey, load]);

  const payload = useMemo(() => (draft && registry ? toUpdate(draft, fields, registry.labels) : null), [draft, fields, registry]);
  const dirty = !!payload && JSON.stringify(payload) !== baseline;

  const patch = (p: Partial<EditorDraft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  // ── validation ───────────────────────────────────────────────────────────
  const fieldIssues = useMemo(() => {
    const out = new Map<string, string[]>();
    const seen = new Set<string>();
    for (const f of fields) {
      const msgs: string[] = [];
      const key = keyOf(f);
      if (!f.name.trim()) msgs.push('Give the field a name.');
      if (!key) msgs.push('The key is empty.');
      else if (seen.has(`${f.entityType}:${key}`)) msgs.push(`Key “${key}” is already used.`);
      if (key) seen.add(`${f.entityType}:${key}`);
      if (hasOptions(f.kind) && !(f.options ?? []).length) msgs.push('Add at least one option.');
      if (msgs.length) out.set(f.uid, msgs);
    }
    return out;
  }, [fields]);

  const issues = useMemo(() => {
    if (!draft) return {} as Partial<Record<TabKey, string[]>>;
    const out: Partial<Record<TabKey, string[]>> = {};
    const push = (t: TabKey, m: string) => { (out[t] ??= []).push(m); };
    if (!draft.name.trim()) push('pages', 'The blueprint needs a name.');
    if (fieldIssues.size) push('fields', `${fieldIssues.size} field${fieldIssues.size === 1 ? '' : 's'} need attention.`);
    if (!draft.pipelineName.trim()) push('pipeline', 'Name the pipeline.');
    const stageNames = draft.stages.map((s) => s.name.trim().toLowerCase());
    if (stageNames.some((n) => !n)) push('pipeline', 'Every stage needs a name.');
    if (new Set(stageNames).size !== stageNames.length) push('pipeline', 'Two stages have the same name.');
    if (!draft.stages.some((s) => s.terminal === 'active')) push('pipeline', 'Add at least one open stage.');
    if (draft.stages.length < 2) push('pipeline', 'A pipeline needs at least two stages.');
    if (draft.stages.filter((s) => s.terminal === 'won').length > 1 || draft.stages.filter((s) => s.terminal === 'lost').length > 1) push('pipeline', 'Use one Won and one Lost stage at most.');
    const deptNames = draft.departments.map((x) => x.name.trim().toLowerCase());
    if (deptNames.some((n) => !n)) push('departments', 'Every department needs a name.');
    if (new Set(deptNames).size !== deptNames.length) push('departments', 'Two departments have the same name.');
    const svcNames = draft.services.map((x) => x.name.trim().toLowerCase());
    if (svcNames.some((n) => !n)) push('services', 'Every service needs a name.');
    if (new Set(svcNames).size !== svcNames.length) push('services', 'Two services have the same name.');
    if (!draft.hours.days.length) push('services', 'Pick at least one open day.');
    if (draft.hours.end <= draft.hours.start) push('services', 'Closing time must be after opening time.');
    return out;
  }, [draft, fieldIssues]);
  const issueCount = Object.values(issues).reduce((n, a) => n + (a?.length ?? 0), 0);

  const save = useCallback(async () => {
    if (!payload || !registry || saving) return;
    if (issueCount) {
      setShowErrors(true);
      const first = (Object.keys(issues) as TabKey[])[0];
      if (first) setTab(first);
      toast.error('Fix the highlighted items before saving');
      return;
    }
    setSaving(true);
    try {
      const b = await blueprintsService.update(familyKey, payload);
      load({ ...b, companies: saved?.companies }, registry);
      setShowErrors(false);
      toast.success(`${b.name} saved — version ${b.version}. Pages and words update for every ${b.name} company now.`);
    } catch { /* interceptor toasts */ } finally { setSaving(false); }
  }, [payload, registry, saving, issueCount, issues, familyKey, load, saved?.companies]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (dirty) void save(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dirty, save]);
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const goBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    nav('/blueprints');
  };

  if (missing) {
    return (
      <>
        <Topbar title="Blueprint not found" breadcrumb="Catalog / Blueprints" backTo="/blueprints" />
        <div className="p-6">
          <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-14 text-center">
            <p className="text-sm text-text-muted">There's no blueprint called <code className="font-mono">{familyKey}</code>.</p>
            <Link to="/blueprints" className="mt-3 inline-block text-sm font-medium text-primary hover:underline">All blueprints</Link>
          </div>
        </div>
      </>
    );
  }

  if (!draft || !registry || !saved) {
    return (
      <>
        <Topbar title="Blueprint" breadcrumb="Catalog / Blueprints" backTo="/blueprints" />
        <div className="mx-auto w-full max-w-[1680px] space-y-6 px-4 py-6 sm:px-7 sm:py-8">
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-[480px] w-full rounded-2xl" />
        </div>
      </>
    );
  }

  const Icon = familyIcon(saved.familyKey);
  const tabs: Array<{ value: TabKey; label: string; count?: number }> = [
    { value: 'pages', label: 'Pages & words' },
    { value: 'fields', label: 'Fields', count: fields.length },
    { value: 'pipeline', label: 'Pipeline', count: draft.stages.length },
    { value: 'departments', label: 'Departments', count: draft.departments.length },
    { value: 'services', label: 'Services & AI', count: draft.services.length },
    { value: 'types', label: 'Types', count: draft.industries.length },
  ];

  return (
    <>
      <Topbar
        title={`${draft.name || 'Untitled'} blueprint`}
        breadcrumb="Catalog / Blueprints"
        description="The CRM a company in this family starts with."
        onBack={goBack}
        right={<Button onClick={save} disabled={!dirty} loading={saving} iconLeft={<Save size={16} />}>Save changes</Button>}
      />
      <div className="mx-auto w-full max-w-[1680px] space-y-6 px-4 py-6 sm:px-7 sm:py-8">
        <div className="flex flex-wrap items-start gap-4 rounded-2xl border border-border bg-surface p-5 shadow-soft">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary">
            <Icon size={28} strokeWidth={1.7} />
          </span>
          <div className="min-w-[240px] flex-1 space-y-2">
            <Input value={draft.name} onChange={(e) => patch({ name: e.target.value })} maxLength={60} aria-label="Blueprint name"
              className="!h-11 font-display !text-lg font-semibold" invalid={showErrors && !draft.name.trim()} />
            <Input value={draft.description} onChange={(e) => patch({ description: e.target.value })} maxLength={300}
              placeholder="Who this setup is for" aria-label="Description" />
          </div>
          <dl className="grid min-w-[220px] grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-text-muted">Version</dt><dd className="text-right font-mono font-medium text-text-primary">v{saved.version}</dd>
            <dt className="text-text-muted">Companies</dt><dd className="text-right font-medium tabular-nums text-text-primary">{saved.companies ?? '—'}</dd>
            <dt className="text-text-muted">Last saved</dt><dd className="text-right text-text-primary">{saved.updatedBy === 'seed' ? 'Starter' : timeAgo(saved.updatedAt)}</dd>
          </dl>
        </div>

        <div className="rounded-2xl border border-border bg-surface shadow-soft">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 pt-4">
            <UnderlineTabs
              label="Blueprint section"
              value={tab}
              onChange={setTab}
              tabs={tabs.map((t) => ({ ...t, dot: showErrors && issues[t.value]?.length ? 'bg-danger' : undefined }))}
            />
            <span className="pb-3 text-xs text-text-muted">⌘S saves</span>
          </div>

          {showErrors && issues[tab]?.length ? (
            <ul className="mx-5 mt-4 space-y-0.5 rounded-xl border border-danger/40 bg-danger/5 px-4 py-3 text-xs text-danger">
              {issues[tab]!.map((m) => <li key={m}>{m}</li>)}
            </ul>
          ) : null}

          <div className="p-5">
            {tab === 'pages' && <PagesTab draft={draft} registry={registry} onChange={patch} />}
            {tab === 'fields' && (
              <FieldsTab
                fields={fields} setFields={setFields} entity={entity} setEntity={setEntity}
                issues={showErrors ? fieldIssues : new Map()} expanded={expanded} setExpanded={setExpanded}
                focusUid={focusUid} setFocusUid={setFocusUid}
              />
            )}
            {tab === 'pipeline' && <PipelineTab draft={draft} onChange={patch} />}
            {tab === 'departments' && <DepartmentsTab draft={draft} onChange={patch} />}
            {tab === 'services' && <ServicesTab draft={draft} onChange={patch} />}
            {tab === 'types' && <TypesTab draft={draft} others={others} onChange={patch} />}
          </div>
        </div>

        {dirty && (
          <div className="sticky bottom-4 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-surface/95 px-5 py-3 shadow-elevated backdrop-blur">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-text-primary">
              <span className="h-2 w-2 rounded-full bg-warning" /> Unsaved changes
              <span className="hidden text-xs font-normal text-text-muted sm:inline">· saving makes version {saved.version + 1}</span>
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => load(saved, registry)}>Discard</Button>
              <Button size="sm" onClick={save} loading={saving}>Save changes</Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Pages & words ─────────────────────────────────────────────────────────────

function PagesTab({ draft, registry, onChange }: { draft: EditorDraft; registry: BlueprintRegistry; onChange: (p: Partial<EditorDraft>) => void }) {
  const groups = useMemo(() => {
    const out: Array<{ group: string; pages: BlueprintRegistry['pages'] }> = [];
    for (const p of registry.pages) {
      let g = out.find((x) => x.group === p.group);
      if (!g) { g = { group: p.group, pages: [] }; out.push(g); }
      g.pages.push(p);
    }
    return out;
  }, [registry.pages]);
  const on = (key: string) => draft.pages[key] ?? registry.pages.find((p) => p.key === key)?.general ?? false;
  const setPage = (key: string, v: boolean) => onChange({ pages: { ...draft.pages, [key]: v } });
  const liveLabels: Record<string, CrmLabel> = useMemo(() => {
    const out: Record<string, CrmLabel> = { ...registry.labels };
    for (const [k, v] of Object.entries(draft.labels)) {
      out[k] = { plural: v.plural.trim() || registry.labels[k]?.plural, singular: v.singular.trim() || registry.labels[k]?.singular };
    }
    return out;
  }, [draft.labels, registry.labels]);
  const setLabel = (key: string, form: 'plural' | 'singular', value: string) =>
    onChange({ labels: { ...draft.labels, [key]: { plural: draft.labels[key]?.plural ?? '', singular: draft.labels[key]?.singular ?? '', [form]: value } } });

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0 space-y-8">
        <section>
          <SectionTitle icon={<LayoutGrid size={16} />} title="Pages" hint="Switched-off pages leave the sidebar of every company in this family. The data stays." />
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.group}>
                <div className="mb-2 flex items-center gap-2 text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">
                  {g.group === 'Operations' ? wordOf(liveLabels, registry.labels, 'operations') : g.group}
                  {g.group === 'Operations' && <span className="rounded bg-primary-soft px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-primary">Industry pages</span>}
                </div>
                <div className="grid gap-2 sm:grid-cols-2 2xl:grid-cols-3">
                  {g.pages.map((p) => {
                    const locked = p.key === 'dashboard';
                    const value = locked || on(p.key);
                    return (
                      <div key={p.key} className={clsx('flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5', value ? 'border-border bg-surface' : 'border-dashed border-border bg-surface-alt')}>
                        <div className="min-w-0">
                          <div className={clsx('truncate text-sm font-medium', value ? 'text-text-primary' : 'text-text-muted')}>
                            {PAGE_WORD[p.key] ? wordOf(liveLabels, registry.labels, PAGE_WORD[p.key]) : p.label}
                          </div>
                          <div className="text-2xs text-text-muted">
                            {locked ? 'Always on' : p.general ? 'Standard CRM page' : 'Only in blueprint families'}
                          </div>
                        </div>
                        {locked ? <Lock size={14} className="shrink-0 text-text-muted" /> : <Switch checked={value} onChange={(v) => setPage(p.key, v)} label={p.label} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <SectionTitle icon={<Tags size={16} />} title="Words" hint="What things are called in this family's CRM. Leave blank to keep the standard word. Display only — data and links don't change." />
          <div className="overflow-hidden rounded-xl border border-border">
            <div className="hidden grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 border-b border-border bg-surface-alt px-4 py-2 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted md:grid">
              <span>Standard word</span><span>Called (plural)</span><span>One of them</span>
            </div>
            {LABEL_ORDER.filter((l) => registry.labels[l.key]).map((l) => {
              const def = registry.labels[l.key];
              const cur = draft.labels[l.key];
              const changed = !!cur && ((cur.plural.trim() && cur.plural.trim() !== def.plural) || (cur.singular.trim() && cur.singular.trim() !== def.singular));
              return (
                <div key={l.key} className="grid gap-2 border-b border-border px-4 py-3 last:border-b-0 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] md:items-center md:gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                      {def.plural}
                      {changed && <span className="h-1.5 w-1.5 rounded-full bg-primary" title="Renamed" />}
                    </div>
                    <div className="truncate text-2xs text-text-muted">{l.where}</div>
                  </div>
                  <Input value={cur?.plural ?? ''} onChange={(e) => setLabel(l.key, 'plural', e.target.value)} placeholder={def.plural} maxLength={40} aria-label={`${def.plural} — plural`} />
                  <Input value={cur?.singular ?? ''} onChange={(e) => setLabel(l.key, 'singular', e.target.value)} placeholder={def.singular} maxLength={40} aria-label={`${def.plural} — singular`} />
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <aside className="xl:sticky xl:top-24 xl:self-start">
        <SidebarPreview registry={registry} labels={liveLabels} on={on} />
      </aside>
    </div>
  );
}

function SidebarPreview({ registry, labels, on }: { registry: BlueprintRegistry; labels: Record<string, CrmLabel>; on: (k: string) => boolean }) {
  const groups: Array<{ title: string; items: string[] }> = [];
  for (const p of registry.pages) {
    if (p.key !== 'dashboard' && !on(p.key)) continue;
    const title = GROUP_WORD[p.group] ? wordOf(labels, registry.labels, GROUP_WORD[p.group]) : p.group;
    let g = groups.find((x) => x.title === title);
    if (!g) { g = { title, items: [] }; groups.push(g); }
    g.items.push(PAGE_WORD[p.key] ? wordOf(labels, registry.labels, PAGE_WORD[p.key]) : p.label);
  }
  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex items-center gap-2 border-b border-border px-5 py-4">
        <Eye size={16} className="text-primary" />
        <h3 className="text-base font-semibold tracking-tight text-text-primary">Sidebar preview</h3>
      </div>
      <div className="max-h-[640px] space-y-3 overflow-y-auto p-4">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="px-2 pb-1 text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{g.title}</div>
            {g.items.map((i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] font-medium text-text-secondary">
                <span className="h-1.5 w-1.5 rounded-full bg-border-strong" /> {i}
              </div>
            ))}
          </div>
        ))}
        <p className="border-t border-border px-2 pt-3 text-2xs text-text-muted">WhatsApp, ads and AI Studio follow the company's module switches.</p>
      </div>
    </div>
  );
}

// ── Fields ────────────────────────────────────────────────────────────────────

function FieldsTab({ fields, setFields, entity, setEntity, issues, expanded, setExpanded, focusUid, setFocusUid }: {
  fields: Draft[]; setFields: React.Dispatch<React.SetStateAction<Draft[]>>;
  entity: TemplateEntityType; setEntity: (e: TemplateEntityType) => void;
  issues: Map<string, string[]>; expanded: Set<string>; setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
  focusUid: string | null; setFocusUid: (u: string | null) => void;
}) {
  const list = fields.filter((f) => f.entityType === entity);
  const patch = (uid: string, p: Partial<Draft>) => setFields((ds) => ds.map((f) => {
    if (f.uid !== uid) return f;
    const next = { ...f, ...p };
    if (p.name !== undefined && !f.keyTouched) next.key = keyOf({ ...next, key: '' });
    return next;
  }));
  const move = (uid: string, dir: -1 | 1) => setFields((ds) => {
    const idx = ds.findIndex((f) => f.uid === uid);
    let j = idx + dir;
    while (j >= 0 && j < ds.length && ds[j].entityType !== ds[idx].entityType) j += dir;
    if (idx < 0 || j < 0 || j >= ds.length) return ds;
    const next = [...ds];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const add = (kind: TemplateFieldKind) => {
    const uid = newUid();
    setFields((ds) => [...ds, { uid, keyTouched: false, entityType: entity, name: '', key: '', kind, options: [], required: false }]);
    setFocusUid(uid);
    if (hasOptions(kind)) setExpanded((s) => new Set(s).add(uid));
  };
  const duplicate = (uid: string) => setFields((ds) => {
    const idx = ds.findIndex((f) => f.uid === uid);
    if (idx < 0) return ds;
    const src = ds[idx];
    const taken = new Set(ds.filter((f) => f.entityType === src.entityType).map(keyOf));
    let n = 2;
    while (taken.has(`${keyOf(src)}_${n}`)) n += 1;
    return [...ds.slice(0, idx + 1), { ...src, uid: newUid(), name: `${src.name} (copy)`, key: `${keyOf(src)}_${n}`, keyTouched: true, options: [...(src.options ?? [])] }, ...ds.slice(idx + 1)];
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-muted">
        Fields every company in this family gets. Each type can add its own on top in{' '}
        <Link to="/industry-configs" className="font-medium text-primary hover:underline">Industry Configs</Link>.
      </p>
      <div className="flex flex-wrap gap-2">
        {ENTITY_ORDER.map((e) => {
          const EIcon = ENTITY_META[e].icon;
          const n = fields.filter((f) => f.entityType === e).length;
          return (
            <button key={e} type="button" onClick={() => setEntity(e)}
              className={clsx('inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                entity === e ? 'border-primary/40 bg-primary-soft text-primary' : 'border-border text-text-secondary hover:border-border-strong')}>
              <EIcon size={14} /> {ENTITY_META[e].label} <span className="tabular-nums text-text-muted">{n}</span>
            </button>
          );
        })}
      </div>
      {list.length === 0 && (
        <div className="rounded-xl border border-dashed border-border px-6 py-8 text-center text-sm text-text-muted">
          No {ENTITY_META[entity].label.toLowerCase()} fields yet.
        </div>
      )}
      {list.map((f, i) => (
        <FieldCard
          key={f.uid}
          field={f}
          first={i === 0}
          last={i === list.length - 1}
          issues={issues.get(f.uid)}
          expanded={expanded.has(f.uid)}
          autoFocus={focusUid === f.uid}
          onChange={(p) => patch(f.uid, p)}
          onMove={(dir) => move(f.uid, dir)}
          onToggle={() => setExpanded((s) => { const n = new Set(s); if (n.has(f.uid)) n.delete(f.uid); else n.add(f.uid); return n; })}
          onDuplicate={() => duplicate(f.uid)}
          onRemove={() => setFields((ds) => ds.filter((x) => x.uid !== f.uid))}
        />
      ))}
      <div className="rounded-xl border border-dashed border-border p-4">
        <p className="mb-3 flex items-center gap-1.5 text-[13px] font-medium text-text-secondary">
          <Plus size={15} /> Add a {ENTITY_META[entity].singular.toLowerCase()} field
        </p>
        <div className="flex flex-wrap gap-2">
          {QUICK_KINDS.map((k) => {
            const KIcon = KIND_META[k].icon;
            return (
              <Button key={k} variant="secondary" size="sm" iconLeft={<KIcon size={14} className="text-primary" />} onClick={() => add(k)}>
                {KIND_META[k].label}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Pipeline ──────────────────────────────────────────────────────────────────

function PipelineTab({ draft, onChange }: { draft: EditorDraft; onChange: (p: Partial<EditorDraft>) => void }) {
  const set = (uid: string, p: Partial<StageDraft>) => onChange({ stages: draft.stages.map((s) => (s.uid === uid ? { ...s, ...p } : s)) });
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= draft.stages.length) return;
    const next = [...draft.stages];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ stages: next });
  };
  const addStage = () => {
    const openCount = draft.stages.filter((s) => s.terminal === 'active').length;
    const stage: StageDraft = { uid: newUid(), name: '', terminal: 'active', probability: 50, color: STAGE_COLORS[openCount % STAGE_COLORS.length], rotDays: '' };
    const firstClosed = draft.stages.findIndex((s) => s.terminal !== 'active');
    const at = firstClosed < 0 ? draft.stages.length : firstClosed;
    onChange({ stages: [...draft.stages.slice(0, at), stage, ...draft.stages.slice(at)] });
  };
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0 space-y-4">
        <SectionTitle icon={<GitBranch size={16} />} title="Deal pipeline" hint="Created as the company's pipeline on assign. Re-apply adds new stages; renamed or removed stages stay as the company left them." />
        <div className="max-w-md">
          <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">Pipeline name</label>
          <Input value={draft.pipelineName} onChange={(e) => onChange({ pipelineName: e.target.value })} maxLength={60} placeholder="Sales" />
        </div>
        <div className="space-y-2">
          {draft.stages.map((s, i) => (
            <div key={s.uid} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface p-3">
              <div className="flex md:flex-col">
                <IconButton label="Move up" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={13} /></IconButton>
                <IconButton label="Move down" disabled={i === draft.stages.length - 1} onClick={() => move(i, 1)}><ArrowDown size={13} /></IconButton>
              </div>
              <input type="color" value={s.color} onChange={(e) => set(s.uid, { color: e.target.value })} aria-label="Stage colour"
                className="h-9 w-9 cursor-pointer rounded-lg border border-border bg-surface p-1" />
              <div className="min-w-[180px] flex-1"><Input value={s.name} onChange={(e) => set(s.uid, { name: e.target.value })} placeholder="Stage name" maxLength={60} aria-label="Stage name" /></div>
              <Select value={s.terminal} onChange={(e) => set(s.uid, { terminal: e.target.value as StageTerminal })} aria-label="Stage type" className="!w-36">
                <option value="active">Open stage</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
              </Select>
              <label className="relative w-24">
                <Input type="number" min={0} max={100} value={s.terminal === 'active' ? s.probability : s.terminal === 'won' ? 100 : 0}
                  disabled={s.terminal !== 'active'} onChange={(e) => set(s.uid, { probability: Number(e.target.value) })} aria-label="Win chance %" className="pr-7" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">%</span>
              </label>
              <label className="relative w-28" title="Flag deals that sit in this stage longer than this">
                <Input type="number" min={1} max={365} value={s.rotDays} disabled={s.terminal !== 'active'}
                  onChange={(e) => set(s.uid, { rotDays: e.target.value })} placeholder="—" aria-label="Flag after days" className="pr-10" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">days</span>
              </label>
              <IconButton label="Remove stage" danger onClick={() => onChange({ stages: draft.stages.filter((x) => x.uid !== s.uid) })}><Trash2 size={14} /></IconButton>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} onClick={addStage}>Add stage</Button>
          <span className="text-xs text-text-muted">Win chance feeds the forecast · “days” flags deals that stall</span>
        </div>
      </div>
      <aside className="xl:sticky xl:top-24 xl:self-start">
        <div className="rounded-2xl border border-border bg-surface shadow-soft">
          <div className="flex items-center gap-2 border-b border-border px-5 py-4">
            <Eye size={16} className="text-primary" />
            <h3 className="text-base font-semibold tracking-tight text-text-primary">Board preview</h3>
          </div>
          <div className="space-y-1.5 p-4">
            <div className="mb-2 text-sm font-semibold text-text-primary">{draft.pipelineName || 'Pipeline'}</div>
            {draft.stages.map((s) => (
              <div key={s.uid} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{s.name || <em className="text-text-muted">Untitled</em>}</span>
                <span className="text-2xs tabular-nums text-text-muted">{s.terminal === 'won' ? 'Won' : s.terminal === 'lost' ? 'Lost' : `${s.probability}%`}</span>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

// ── Departments ───────────────────────────────────────────────────────────────

function DepartmentsTab({ draft, onChange }: { draft: EditorDraft; onChange: (p: Partial<EditorDraft>) => void }) {
  const set = (uid: string, p: Partial<DeptDraft>) => onChange({ departments: draft.departments.map((d) => (d.uid === uid ? { ...d, ...p } : d)) });
  return (
    <div className="space-y-4">
      <SectionTitle icon={<Users size={16} />} title="Departments" hint="Teams new leads route between. The first one is where new leads land. Re-apply adds missing ones; a department the company deleted stays deleted." />
      <div className="space-y-2">
        {draft.departments.map((d, i) => (
          <div key={d.uid} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface p-3">
            <input type="color" value={d.color} onChange={(e) => set(d.uid, { color: e.target.value })} aria-label="Colour"
              className="h-9 w-9 cursor-pointer rounded-lg border border-border bg-surface p-1" />
            <div className="relative min-w-[160px] flex-1">
              <Input value={d.name} onChange={(e) => set(d.uid, { name: e.target.value })} placeholder="Department name" maxLength={60} aria-label="Department name" />
              {i === 0 && <span className="absolute -top-2 right-2 rounded bg-primary px-1.5 text-[10px] font-semibold text-white">Leads land here</span>}
            </div>
            <Select value={d.kind} onChange={(e) => set(d.uid, { kind: e.target.value as DeptKind })} aria-label="Kind" className="!w-44">
              {DEPT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </Select>
            <div className="min-w-[200px] flex-[2]"><Input value={d.description} onChange={(e) => set(d.uid, { description: e.target.value })} placeholder="What this team handles" maxLength={200} aria-label="Description" /></div>
            <IconButton label="Remove department" danger onClick={() => onChange({ departments: draft.departments.filter((x) => x.uid !== d.uid) })}><Trash2 size={14} /></IconButton>
          </div>
        ))}
      </div>
      <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />}
        onClick={() => onChange({ departments: [...draft.departments, { uid: newUid(), name: '', kind: 'custom', description: '', color: '#a18a44' }] })}>
        Add department
      </Button>
    </div>
  );
}

// ── Services & AI ─────────────────────────────────────────────────────────────

function ServicesTab({ draft, onChange }: { draft: EditorDraft; onChange: (p: Partial<EditorDraft>) => void }) {
  const set = (uid: string, p: Partial<ServiceDraft>) => onChange({ services: draft.services.map((s) => (s.uid === uid ? { ...s, ...p } : s)) });
  const toggleDay = (d: number) => onChange({ hours: { ...draft.hours, days: draft.hours.days.includes(d) ? draft.hours.days.filter((x) => x !== d) : [...draft.hours.days, d] } });
  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <SectionTitle icon={<Sparkles size={16} />} title="Sample services" hint="Items marked “In catalog” are added to the company's Catalog (its menu or services list). Bookable ones also become appointment services with the hours below. Prices use the company's currency." />
        <div className="space-y-2">
          {draft.services.map((s) => (
            <div key={s.uid} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface p-3">
              <div className="min-w-[180px] flex-[2]"><Input value={s.name} onChange={(e) => set(s.uid, { name: e.target.value })} placeholder="Service name" maxLength={80} aria-label="Service name" /></div>
              <div className="min-w-[120px] flex-1"><Input value={s.category} onChange={(e) => set(s.uid, { category: e.target.value })} placeholder="Category" maxLength={60} aria-label="Category" /></div>
              <label className="relative w-28">
                <Input type="number" min={5} max={1440} value={s.durationMinutes} onChange={(e) => set(s.uid, { durationMinutes: e.target.value })} aria-label="Duration in minutes" className="pr-10" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">min</span>
              </label>
              <div className="w-32"><Input type="number" min={0} step="0.01" value={s.price} onChange={(e) => set(s.uid, { price: e.target.value })} placeholder="Price (free)" aria-label="Price" /></div>
              <button type="button" onClick={() => set(s.uid, { bookable: !s.bookable })} aria-pressed={s.bookable}
                className={clsx('inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors',
                  s.bookable ? 'border-primary/40 bg-primary-soft text-primary' : 'border-border text-text-muted hover:text-text-primary')}>
                <CalendarClock size={13} /> {s.bookable ? 'Bookable' : 'Not bookable'}
              </button>
              {!s.bookable && (
                <Select value={s.kind} onChange={(e) => set(s.uid, { kind: e.target.value as ServiceDraft['kind'] })} aria-label="Kind" className="!w-32">
                  <option value="product">Product</option>
                  <option value="service">Service</option>
                </Select>
              )}
              <button type="button" onClick={() => set(s.uid, { inCatalog: !s.inCatalog })} aria-pressed={s.inCatalog}
                title={s.inCatalog ? 'Added to the Catalog' : 'Booking only — not listed in the Catalog'}
                className={clsx('inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors',
                  s.inCatalog ? 'border-primary/40 bg-primary-soft text-primary' : 'border-border text-text-muted hover:text-text-primary')}>
                <BookOpen size={13} /> {s.inCatalog ? 'In catalog' : 'Not in catalog'}
              </button>
              <IconButton label="Remove service" danger onClick={() => onChange({ services: draft.services.filter((x) => x.uid !== s.uid) })}><Trash2 size={14} /></IconButton>
            </div>
          ))}
        </div>
        <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />}
          onClick={() => onChange({ services: [...draft.services, { uid: newUid(), name: '', category: '', durationMinutes: '30', price: '', description: '', bookable: true, kind: 'service', inCatalog: true }] })}>
          Add service
        </Button>

        <div className="rounded-xl border border-border bg-bg/60 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-primary"><Clock size={15} className="text-primary" /> Booking hours for bookable services</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((w, d) => (
                <button key={w} type="button" onClick={() => toggleDay(d)} aria-pressed={draft.hours.days.includes(d)}
                  className={clsx('h-9 w-12 rounded-lg border text-xs font-semibold transition-colors',
                    draft.hours.days.includes(d) ? 'border-primary bg-primary text-white' : 'border-border bg-surface text-text-muted hover:text-text-primary')}>
                  {w}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <Input type="time" value={draft.hours.start} onChange={(e) => onChange({ hours: { ...draft.hours, start: e.target.value } })} aria-label="Opens" className="!w-32" />
              to
              <Input type="time" value={draft.hours.end} onChange={(e) => onChange({ hours: { ...draft.hours, end: e.target.value } })} aria-label="Closes" className="!w-32" />
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <SectionTitle icon={<Bot size={16} />} title="WhatsApp reply mode" hint="Saved on each company when the blueprint is first applied. New WhatsApp numbers start in this mode once the WhatsApp link is switched on." />
        <div className="grid gap-2 md:grid-cols-3">
          {(Object.keys(BOT_MODE_META) as BotMode[]).map((m) => (
            <button key={m} type="button" onClick={() => onChange({ aiBotMode: m })} aria-pressed={draft.aiBotMode === m}
              className={clsx('flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors',
                draft.aiBotMode === m ? 'border-primary bg-primary-soft/60' : 'border-border hover:border-border-strong')}>
              <span className={clsx('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', draft.aiBotMode === m ? 'border-primary bg-primary text-white' : 'border-border-strong')}>
                {draft.aiBotMode === m && <Check size={10} strokeWidth={3} />}
              </span>
              <span>
                <span className="block text-sm font-semibold text-text-primary">{BOT_MODE_META[m].label}</span>
                <span className="mt-0.5 block text-xs text-text-muted">{BOT_MODE_META[m].hint}</span>
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

function TypesTab({ draft, others, onChange }: { draft: EditorDraft; others: Blueprint[]; onChange: (p: Partial<EditorDraft>) => void }) {
  const ownerOf = (k: string) => others.find((b) => b.industries.includes(k));
  const groups = useMemo(() => {
    const out: Array<{ group: string; items: typeof INDUSTRY_OPTIONS }> = [];
    for (const o of INDUSTRY_OPTIONS) {
      if (o.value === 'other') continue;
      let g = out.find((x) => x.group === o.group);
      if (!g) { g = { group: o.group, items: [] }; out.push(g); }
      g.items.push(o);
    }
    return out;
  }, []);
  const toggle = (k: string) => onChange({ industries: draft.industries.includes(k) ? draft.industries.filter((x) => x !== k) : [...draft.industries, k] });
  return (
    <div className="space-y-5">
      <SectionTitle icon={<Building2 size={16} />} title="Types in this family" hint="Companies with one of these industries use this blueprint. Removing a type sends its companies back to the standard pages and words — what was copied stays." />
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {groups.map((g) => (
          <div key={g.group}>
            <div className="mb-2 text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{g.group}</div>
            <div className="space-y-1.5">
              {g.items.map((o) => {
                const owner = ownerOf(o.value);
                const checked = draft.industries.includes(o.value);
                const TIcon = industryIcon(o.value, o.group);
                return (
                  <button key={o.value} type="button" disabled={!!owner} onClick={() => toggle(o.value)} aria-pressed={checked}
                    className={clsx('flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      checked ? 'border-primary/40 bg-primary-soft text-primary' : 'border-border text-text-secondary hover:border-border-strong',
                      owner && 'cursor-not-allowed opacity-50')}>
                    <TIcon size={15} className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate font-medium">{o.label}</span>
                    {owner ? <span className="text-2xs text-text-muted">In {owner.name}</span> : checked && <Check size={14} />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Small pieces ──────────────────────────────────────────────────────────────

function SectionTitle({ icon, title, hint }: { icon: React.ReactNode; title: string; hint: string }) {
  return (
    <div className="mb-3 flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">{icon}</span>
      <div>
        <h3 className="text-base font-semibold tracking-tight text-text-primary">{title}</h3>
        <p className="mt-0.5 max-w-3xl text-xs leading-relaxed text-text-muted">{hint}</p>
      </div>
    </div>
  );
}

function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
      className={clsx('relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        checked ? 'bg-primary' : 'bg-border-strong')}>
      <span className={clsx('inline-block h-4 w-4 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-[18px]' : 'translate-x-0.5')} />
    </button>
  );
}

function IconButton({ children, label, onClick, disabled, danger }: { children: React.ReactNode; label: string; onClick: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick}
      className={clsx('flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition-colors disabled:opacity-30',
        danger ? 'hover:bg-danger/10 hover:text-danger' : 'hover:bg-surface-alt hover:text-text-primary')}>
      {children}
    </button>
  );
}

